/**
 * Geolocation Service
 * 
 * Captures IP address and geographic information for analytics.
 * Uses two-step approach: IP first (critical), geo second (optional)
 * This ensures IP is always captured even if geo lookup fails.
 */

/**
 * Geographic information
 */
export interface GeoInfo {
  ip_address: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
}

/**
 * Session cache for geo data (prevents duplicate API calls)
 */
let cachedGeoInfo: GeoInfo | null = null;
let geoFetchPromise: Promise<GeoInfo> | null = null;

/**
 * Get IP address only (fast, lightweight)
 * Uses ipify.org as primary, falls back to ipapi.co if needed
 * This ensures we ALWAYS capture IP even if geo lookup fails
 * 
 * @returns Promise resolving to IP address or null
 */
export async function getIPAddress(): Promise<string | null> {
  try {
    // Try ipify.org first (fast, simple, reliable)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      if (data?.ip && typeof data.ip === 'string') {
        return data.ip;
      }
    }
  } catch (error) {
    // ipify failed, try fallback
  }
  
  // Fallback to ipapi.co IP-only endpoint
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch('https://ipapi.co/ip/', {
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const ip = await response.text();
      if (ip && ip.trim()) {
        return ip.trim();
      }
    }
  } catch (error) {
    // Both services failed
  }
  
  return null;
}

/**
 * Get geolocation data only (country, region, city)
 * Does NOT include IP address - use getIPAddress() for that
 * 
 * @returns Promise resolving to geo data
 */
export async function getGeoData(): Promise<{
  country: string | null;
  region: string | null;
  city: string | null;
}> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      return { country: null, region: null, city: null };
    }
    
    const data = await response.json();
    
    return {
      country: data.country_name || data.country || null,
      region: data.region || data.region_code || null,
      city: data.city || null,
    };
  } catch (error) {
    return { country: null, region: null, city: null };
  }
}

/**
 * Get IP and Geo data together (with caching)
 * IP is fetched separately first to ensure it's captured even if geo fails
 * 
 * @returns Promise resolving to complete geo info with IP
 */
export async function getIPAndGeo(): Promise<GeoInfo> {
  // Return cached result if available (within session)
  if (cachedGeoInfo) {
    return cachedGeoInfo;
  }

  // If a fetch is already in progress, return that promise
  if (geoFetchPromise) {
    return geoFetchPromise;
  }

  // Start new fetch with two-step approach
  geoFetchPromise = (async () => {
    // Step 1: Get IP first (critical - has fallback)
    const ipPromise = getIPAddress().catch(() => null);
    
    // Step 2: Get geo data separately (optional - can fail without losing IP)
    const geoPromise = getGeoData().catch(() => ({
      country: null,
      region: null,
      city: null,
    }));

    // Execute both in parallel but handle independently
    const [ip_address, geoData] = await Promise.all([ipPromise, geoPromise]);

    const result: GeoInfo = {
      ip_address,
      country: geoData.country,
      region: geoData.region,
      city: geoData.city,
    };

    cachedGeoInfo = result;
    geoFetchPromise = null;
    return result;
  })();

  return geoFetchPromise;
}

/**
 * Gets user agent string
 * 
 * @returns User agent string or 'Unknown' if not available
 */
export function getUserAgent(): string {
  return typeof navigator !== 'undefined' && navigator.userAgent 
    ? navigator.userAgent 
    : 'Unknown';
}

